import { type ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { AppButton } from '@/components/common/AppButton';

export interface AppModalProps {
  open: boolean;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  footerLayout?: 'default' | 'split';
  panelClassName?: string;
  onClose: () => void;
}

export const AppModal = ({
  open,
  title,
  children,
  footer,
  footerLayout = 'default',
  panelClassName,
  onClose,
}: AppModalProps) => {
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 grid place-items-center bg-[#0B1F4A]/60 p-4" role="presentation" onMouseDown={onClose}>
      <section
        className={clsx(
          'flex max-h-[calc(100dvh-2rem)] w-full max-w-[34rem] flex-col overflow-hidden rounded-2xl bg-white shadow-[0_24px_60px_rgba(11,31,74,0.22)]',
          panelClassName,
        )}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="flex shrink-0 items-center justify-between gap-4 px-5 pt-5 pb-0">
          <h2 className="m-0 text-lg font-semibold text-[#0B1F4A]">{title}</h2>
          <AppButton aria-label="Close modal" size="sm" variant="ghost" onClick={onClose}>
            Close
          </AppButton>
        </header>
        <div className="min-h-0 overflow-y-auto px-5 py-4 text-[#46556E]">{children}</div>
        {footer && (
          <div
            className={clsx(
              'grid shrink-0 gap-3 border-t border-slate-100 px-5 pb-5 pt-4 [&>*]:w-full',
              footerLayout === 'split' ? 'grid-cols-2 [&>*]:min-w-0' : 'grid-cols-1',
            )}
          >
            {footer}
          </div>
        )}
      </section>
    </div>,
    document.body,
  );
};
